import { z } from "zod";

export abstract class EcwError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = new.target.name;
  }
}

/** Marks a WebECW failure that occurred before any mutation request was dispatched. */
export class EcwPreDispatchError extends EcwError {
  constructor(cause: unknown) {
    super("WebECW operation failed before dispatch", { cause });
  }
}

export class EcwDeadlineExceededError extends EcwError {
  constructor(
    public readonly operation: string,
    public readonly deadlineMs: number
  ) {
    super(`WebECW ${operation} exceeded its ${deadlineMs}ms deadline`);
  }
}

export enum EcwSessionDeadReason {
  REDIRECT_SAML = "redirect-saml",
  STATUS_401 = "status-401",
  STATUS_403 = "status-403",
  PARSE_HTML_LOGIN = "parse-html-login",
  PARSE_NOT_JSON = "parse-not-json",
  FILE_TRANSFER_AES_STALE = "file-transfer-aes-stale",
  TOO_MANY_MINT_FAILURES = "too-many-mint-failures",
}

export class EcwSessionDeadError extends EcwError {
  constructor(
    public readonly reason: EcwSessionDeadReason,
    message: string
  ) {
    super(message);
  }
}

export enum EcwResponseBodyKind {
  EMPTY = "empty",
  HTML = "html",
  JSON = "json",
  TEXT = "text",
  XML = "xml",
}

export function classifyEcwResponseBody(text: string): EcwResponseBodyKind {
  const trimmed = text.trim();

  if (!trimmed) return EcwResponseBodyKind.EMPTY;
  if (/^<!doctype html|^<html/i.test(trimmed)) return EcwResponseBodyKind.HTML;
  if (trimmed.startsWith("<")) return EcwResponseBodyKind.XML;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return EcwResponseBodyKind.JSON;

  return EcwResponseBodyKind.TEXT;
}

export class EcwHttpStatusError extends EcwError {
  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly responseBodyKind?: EcwResponseBodyKind
  ) {
    super(`eCW endpoint ${path} returned ${status}`);
  }
}

export class EcwChecksumError extends EcwError {
  constructor(
    public readonly path: string,
    options: ErrorOptions
  ) {
    super(`eCW request checksum computation failed for ${path}`, options);
  }
}

export interface EcwValidationIssue {
  code: string;
  keys?: string[];
  path: string;
}

export enum EcwResponseParseStage {
  JSON_DECODE = "json_decode",
  RESPONSE_BODY = "response_body",
  RESPONSE_SCHEMA = "response_schema",
  SOAP_ENVELOPE = "soap_envelope",
  XML_DECODE = "xml_decode",
  UNKNOWN = "unknown",
}

export enum EcwResponseParseReason {
  INVALID_FIELD_TYPE = "invalid_field_type",
  INVALID_JSON = "invalid_json",
  MALFORMED_XML = "malformed_xml",
  MISSING_REQUIRED_FIELD = "missing_required_field",
  MISSING_SOAP_BODY = "missing_soap_body",
  MISSING_SOAP_ENVELOPE = "missing_soap_envelope",
  MISSING_SOAP_RETURN = "missing_soap_return",
  SCHEMA_VALIDATION = "schema_validation",
  UNKNOWN = "unknown",
  UNRECOGNIZED_KEYS = "unrecognized_keys",
}

export interface EcwResponseShape {
  bodyKeys: string[];
  envelopeKeys: string[];
  returnKeys: string[];
  returnKind: string;
  rootKeys: string[];
  structuralFingerprint: string;
}

const MAX_VALIDATION_ISSUES = 10;
const MAX_UNRECOGNIZED_KEYS = 10;

export function safeEcwResponseKey(value: string): string {
  return /^[A-Za-z_][A-Za-z0-9_.-]{0,63}$/.test(value) ? value : "<dynamic>";
}

function validationPath(path: Array<string | number>): string {
  return path
    .map((segment) => (typeof segment === "number" ? "[]" : safeEcwResponseKey(segment)))
    .join(".")
    .replaceAll(".[]", "[]");
}

export function summarizeEcwValidationIssues(error: unknown): EcwValidationIssue[] {
  if (!(error instanceof z.ZodError)) return [];

  return error.issues.slice(0, MAX_VALIDATION_ISSUES).map((issue) => ({
    code: issue.code,
    ...(issue.code === z.ZodIssueCode.unrecognized_keys
      ? { keys: issue.keys.slice(0, MAX_UNRECOGNIZED_KEYS).map(safeEcwResponseKey) }
      : {}),
    path: validationPath(issue.path),
  }));
}

export function classifyEcwValidationFailure(error: unknown): EcwResponseParseReason {
  if (!(error instanceof z.ZodError)) return EcwResponseParseReason.UNKNOWN;

  if (
    error.issues.some(
      (issue) => issue.code === z.ZodIssueCode.invalid_type && issue.received === z.ZodParsedType.undefined
    )
  ) {
    return EcwResponseParseReason.MISSING_REQUIRED_FIELD;
  }

  if (error.issues.some((issue) => issue.code === z.ZodIssueCode.invalid_type)) {
    return EcwResponseParseReason.INVALID_FIELD_TYPE;
  }

  if (error.issues.some((issue) => issue.code === z.ZodIssueCode.unrecognized_keys)) {
    return EcwResponseParseReason.UNRECOGNIZED_KEYS;
  }

  return EcwResponseParseReason.SCHEMA_VALIDATION;
}

interface EcwResponseParseErrorOptions {
  parseReason?: EcwResponseParseReason;
  parseStage?: EcwResponseParseStage;
  responseBodyKind?: EcwResponseBodyKind;
  responseBytes?: number;
  responseShape?: EcwResponseShape;
  validationIssues?: EcwValidationIssue[];
}

export class EcwResponseParseError extends EcwError {
  public readonly parseReason: EcwResponseParseReason;
  public readonly parseStage: EcwResponseParseStage;
  public readonly responseBodyKind?: EcwResponseBodyKind;
  public readonly responseBytes?: number;
  public readonly responseShape?: EcwResponseShape;
  public readonly validationIssues: EcwValidationIssue[];

  constructor(
    public readonly path: string,
    public readonly status: number,
    options: EcwResponseParseErrorOptions = {}
  ) {
    // Do not retain the parser error as a cause. Zod custom messages can contain raw eCW response
    // values, and generic error serialization walks the cause chain into logs.
    super(`eCW response failed to parse for ${path} (status ${status})`);
    this.parseReason = options.parseReason ?? EcwResponseParseReason.UNKNOWN;
    this.parseStage = options.parseStage ?? EcwResponseParseStage.UNKNOWN;
    this.responseBodyKind = options.responseBodyKind;
    this.responseBytes = options.responseBytes;
    this.responseShape = options.responseShape;
    this.validationIssues = options.validationIssues ?? [];
  }
}

export function ecwResponsePreview(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();

  return collapsed.length > 120 ? `${collapsed.slice(0, 120)}...` : collapsed;
}

export class EcwOperationFailedError extends EcwError {
  constructor(
    public readonly path: string,
    public readonly detail: string
  ) {
    super(`eCW operation did not succeed for ${path}: ${detail}`);
  }
}

export function assertEcwStatusSuccess(path: string, response: { status: string }): void {
  if (response.status.toLowerCase() !== "success") {
    throw new EcwOperationFailedError(path, `unexpected status: ${ecwResponsePreview(response.status)}`);
  }
}

export class EcwSessionCryptoError extends EcwError {
  constructor(
    public readonly field: string,
    public readonly detail: string
  ) {
    super(`eCW session crypto material invalid (${field}): ${detail}`);
  }
}

export class EcwSessionStoreError extends EcwError {
  constructor(
    public readonly operation: string,
    public readonly groupId: string | null,
    options: ErrorOptions
  ) {
    super(`eCW session store operation "${operation}" failed${groupId ? ` for group ${groupId}` : ""}`, options);
  }
}

export enum EcwSessionConfigurationFailureReason {
  MISSING_LIVE_OWNER_GROUP = "missing_live_owner_group",
  MISSING_SESSION_CONFIG = "missing_session_config",
}

export class EcwSessionConfigurationError extends EcwError {
  constructor(
    public readonly groupId: string,
    public readonly reason: EcwSessionConfigurationFailureReason
  ) {
    super(`eCW session configuration invalid for group ${groupId} (${reason})`);
  }
}

export enum EcwSessionMiss {
  NOT_READY = "not-ready",
  DEAD = "dead",
}

export class EcwSessionNotReadyError extends EcwError {
  constructor(
    public readonly groupId: string,
    public readonly miss: EcwSessionMiss,
    public readonly reason?: EcwSessionDeadReason
  ) {
    super(`no working eCW session for group ${groupId} (${miss}${reason ? `: ${reason}` : ""})`);
  }
}

export class EcwFarmTriggerError extends EcwError {
  constructor(
    public readonly groupId: string,
    public readonly status: number | null,
    options?: ErrorOptions
  ) {
    super(`eCW farm Job trigger failed for group ${groupId}${status === null ? "" : ` (status ${status})`}`, options);
  }
}
